import React, { useState, useEffect, useRef } from "react";
// import { useLocation } from "react-router-dom";
import ChatWindow from "./components/ChatWindow";
import "./App.css";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { ReactComponent as BBlogo } from "./Assets/blazebot-logo.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane } from "@fortawesome/free-solid-svg-icons";

function findLastUserMessage(conversation) {
  for (let i = conversation.length - 1; i >= 0; i--) {
    if (conversation[i].Role === "user") {
      return conversation[i];
    }
  }
  // If no element satisfies the condition, return null or handle the case accordingly
  return null;
}

// Default intro messages if none provided by API
const DEFAULT_INTRO_MESSAGES = [
  "Welcome! No Introduction messages have been created for this agent. Please ask your question.",
];
const FULLSCREEN_WIDTH_THRESHOLD_PX = 900;

// Helper function to get initial conversation for an agent
const getInitialConversation = (agentId, agentConfigs) => {
  const config = agentConfigs[agentId];
  if (!config) {
    return [];
  }

  // Use default messages if none provided
  const introMessages =
    config.introMessages && config.introMessages.length > 0 ? config.introMessages : DEFAULT_INTRO_MESSAGES;

  return introMessages.map((message, index) => ({
    ChatId: 1,
    Message: message,
    Role: "assistant",
    introductionMessage: true,
    // Add response options only to the last message
    ...(index === introMessages.length - 1 && { responsesOptions: config.responseOptions }),
  }));
};

const App = ({ onClose, onToggleExpand, isExpanded = false }) => {
  // Read the 'agent' URL parameter (by name)
  let agentParam = null;
  try {
    const params = new URLSearchParams(window.location.search);
    agentParam = params.get("agent");
  } catch (e) {
    // Ignore errors
  }
  const themeMode = window.__ASKBLAZE_THEME_MODE__ === "dark" ? "dark" : "light";

  const [message, setMessage] = useState("");
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentConfigs, setAgentConfigs] = useState({});
  const [agentsList, setAgentsList] = useState([]);
  const [conversation, setConversation] = useState([]);
  const [showLoading, setShowLoading] = useState(false);
  const [isAgentSelectOpen, setIsAgentSelectOpen] = useState(false);
  const [appWidth, setAppWidth] = useState(0);
  const wasFullscreenRef = useRef(null);
  const appContainerRef = useRef(null);
  const chatEndRef = useRef(null);
  const abortControllerRef = useRef(null);
  const API_URL = `https://${process.env.REACT_APP_API_BASE}/api/PostChat?code=${process.env.REACT_APP_API_CHAT_KEY}`;
  const API_FEEDBACK_URL = `https://${process.env.REACT_APP_API_BASE}/api/PostFeedback?code=${process.env.REACT_APP_API_FEEDBACK_KEY}`;
  const API_AGENTS_URL = `https://${process.env.REACT_APP_API_BASE}/api/GetAgentsList?code=${process.env.REACT_APP_API_GET_AGENTS_KEY}`;

  // Fetch agents on component mount
  useEffect(() => {
    fetch(API_AGENTS_URL)
      .then((response) => response.json())
      .then((result) => {
        // Filter out archived agents and build configs
        const activeAgents = result.data.filter((agent) => !agent.archived);
        const configs = {};
        activeAgents.forEach((agent) => {
          configs[agent.id] = {
            introMessages: agent.introMessages,
            responseOptions: agent.responseOptions,
            agentName: agent.agentName,
          };
        });
        setAgentConfigs(configs);
        setAgentsList(activeAgents);

        // Find agent by name from URL param, fallback to Blazebot (id 1 or name)
        let defaultAgentId = null;
        if (agentParam) {
          // Try to find agent by name (case-insensitive)
          const found = activeAgents.find((a) => a.agentName.toLowerCase() === agentParam.toLowerCase());
          if (found) {
            defaultAgentId = found.id;
          }
        }
        // If not found, fallback to Blazebot (id 1 or name)
        if (!defaultAgentId) {
          const blazeBot = activeAgents.find((a) => a.id === 1 || a.agentName.toLowerCase() === "blazebot");
          if (blazeBot) {
            defaultAgentId = blazeBot.id;
          } else if (activeAgents.length > 0) {
            defaultAgentId = activeAgents[0].id;
          }
        }
        if (defaultAgentId) {
          setSelectedAgent(defaultAgentId);
          setConversation(getInitialConversation(defaultAgentId, configs));
        }
      })
      .catch((error) => {
        console.error("Error fetching agents:", error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!appContainerRef.current) {
      return;
    }

    const node = appContainerRef.current;

    const updateSize = () => {
      const rect = node.getBoundingClientRect();
      setAppWidth(Math.round(rect.width));
    };

    updateSize();

    const observer = new ResizeObserver(updateSize);
    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, []);

  const isFullscreenByWidth = appWidth >= FULLSCREEN_WIDTH_THRESHOLD_PX;

  useEffect(() => {
    if (wasFullscreenRef.current === null) {
      wasFullscreenRef.current = isFullscreenByWidth;
      console.log(
        `[App Resize] Initial mode is ${isFullscreenByWidth ? "fullscreen" : "normal"} by width. appWidth=${appWidth}, viewportWidth=${window.innerWidth}`,
      );
      return;
    }

    if (wasFullscreenRef.current !== isFullscreenByWidth) {
      console.log(
        `[App Resize] Switched to ${isFullscreenByWidth ? "fullscreen" : "normal"} mode by width. appWidth=${appWidth}, viewportWidth=${window.innerWidth}`,
      );
      wasFullscreenRef.current = isFullscreenByWidth;
    }
  }, [isFullscreenByWidth, appWidth]);

  const updateMessage = (event) => setMessage(event.target.value);

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      sendMessage(event);
    }
  };

  const sendMessage = (event) => {
    event.preventDefault();
    if (message !== "") {
      setConversation([
        ...conversation,
        { ChatId: 1, Message: message, Role: "user", SentAt: new Date().toISOString() },
      ]);

      setMessage("");
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
    if (conversation.length && conversation[conversation.length - 1].Role === "user") {
      setShowLoading(true);

      var userMessage = findLastUserMessage(conversation);
      var messageHistory = [];
      if (conversation.length > 5) {
        messageHistory = conversation.slice(conversation.length - 5);
      } else {
        messageHistory = conversation.slice(0);
      }
      if (messageHistory.length > 1) {
        messageHistory.splice(-1);
      }

      var userId = sessionStorage.getItem('userId') || '';
      var userType = sessionStorage.getItem('object') || '';
      var post = JSON.stringify({ AgentID: selectedAgent, History: messageHistory, ChatText: userMessage.Message, BlazerID: userId, UserType: userType });

      // Create new AbortController for this request
      abortControllerRef.current = new AbortController();

      //console.log("Message Post\n" + post);
      fetch(API_URL, {
        method: "POST",
        body: post,
        headers: { "Content-Type": "application/json" },
        signal: abortControllerRef.current.signal,
      })
        .then((response) =>
          response.ok
            ? response.json()
            : "I'm sorry, I'm having trouble connecting to the server. Please try back later.",
        )
        .then((data) => {
          setConversation([...conversation, { ChatId: data.id, Message: data.result, Role: "assistant" }]);
        })
        .catch((error) => {
          // Don't show error if request was aborted (agent switched)
          if (error.name === "AbortError") {
            console.log("Request aborted due to agent change");
            return;
          }
          console.error("Error:", error);
          setConversation([
            ...conversation,
            {
              ChatId: 1,
              Message: "I'm sorry, I'm having trouble connecting to the server. Please try back later.",
              Role: "assistant",
            },
          ]);
        })
        .finally(() => {
          setShowLoading(false);
        });
    }
    chatEndRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation, selectedAgent]);

  const handleFeedbackSubmit = (id, response, feedback) => {
    console.log("Feedback for:", response);
    console.log("Feedback details:", feedback);
    // Save feedback here (e.g., send to a server or store locally)
    var post = JSON.stringify({ chatId: id, rating: feedback.rating, comment: feedback.comment });
    console.log(post);
    fetch(API_FEEDBACK_URL, {
      method: "POST",
      body: post,
      headers: { "Content-Type": "application/json" },
    });
  };

  const handleOptionClick = (value) => {
    setConversation([...conversation, { ChatId: 1, Message: value, Role: "user", SentAt: new Date().toISOString() }]);
  };

  const handleDropdownChange = (event) => {
    const newAgentId = event.target.value;

    // Cancel any pending API calls
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    setSelectedAgent(newAgentId);
    setShowLoading(false);

    // Reset conversation with the new agent's configuration
    setConversation(getInitialConversation(newAgentId, agentConfigs));
    setMessage("");
  };

  return (
    <Box
      ref={appContainerRef}
      className="app-container"
      data-app-width={appWidth}
      role="main"
      aria-label="Main chat area"
      sx={{
        height: onClose ? "100%" : "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: themeMode === "dark" ? "#1A5632" : "#FFFFFF",
        // borderRadius: "8px",
      }}
    >
      {/* Visually hidden main heading for accessibility */}
      <Typography
        variant="h1"
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: "hidden",
          clip: "rect(0,0,0,0)",
          border: 0,
        }}
        tabIndex={-1}
      >
        BlazeBot Chat
      </Typography>
      {/* Header Area */}
      <Box
        component="header"
        role="banner"
        aria-label="Header"
        sx={{
          width: "100%",
          // height: "60px",
          height: isFullscreenByWidth ? "65px" : "8%",
          backgroundColor: "#1A5632",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          borderBottom: `1px solid ${themeMode === "dark" ? "#033319" : "#e0e0e0"}`,
        }}
      >
        <BBlogo
          style={{
            height: "200%",
            maxHeight: "120px",
          }}
        />
        {process.env.REACT_APP_AGENT_PICKER === "true" && (
          <>
            {/* Visually hidden heading for agent picker */}
            <Typography
              variant="h2"
              style={{
                position: "absolute",
                width: 1,
                height: 1,
                padding: 0,
                margin: -1,
                overflow: "hidden",
                clip: "rect(0,0,0,0)",
                border: 0,
              }}
              tabIndex={-1}
            >
              Agent Picker
            </Typography>
            <FormControl
              sx={{
                marginLeft: "10px",
                marginRight: onClose ? "20px" : "3%",
                minWidth: 120,
              }}
            >
              {/* Visually hidden InputLabel for accessibility */}
              <InputLabel
                id="agent-picker-label"
                style={{
                  position: "absolute",
                  width: "1px",
                  height: "1px",
                  padding: 0,
                  margin: "-1px",
                  overflow: "hidden",
                  clip: "rect(0,0,0,0)",
                  border: 0,
                }}
              >
                Agent picker
              </InputLabel>
              <Select
                labelId="agent-picker-label"
                id="agent-picker"
                value={selectedAgent}
                onChange={handleDropdownChange}
                onOpen={() => setIsAgentSelectOpen(true)}
                onClose={() => setIsAgentSelectOpen(false)}
                inputProps={{ "aria-label": "Agent selector" }}
                displayEmpty
                sx={{
                  color: "#222222",
                  backgroundColor: "#fdb913",
                  borderColor: "#fdb913",
                  borderRadius: "999px",
                  padding: "2px 8px",
                  fontSize: "0.95rem",
                  fontWeight: 500,
                  transition: "all 0.2s ease-in-out",
                  minHeight: "60%",
                  "& .MuiSelect-select": {
                    paddingTop: "6px",
                    paddingBottom: "6px",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: isAgentSelectOpen ? "#b8860b" : "#fdb913",
                    borderRadius: "999px",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#b8860b",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: isAgentSelectOpen ? "#b8860b" : "#fdb913",
                    borderWidth: "2px",
                  },
                  "&.Mui-focused:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#b8860b",
                  },
                  "& .MuiSelect-icon": {
                    color: "#222222",
                  },
                  "& .MuiSvgIcon-root": {
                    color: "#222222",
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      backgroundColor: "#ffffff",
                      marginTop: "8px",
                      borderRadius: "8px",
                      boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)",
                      "& .MuiMenuItem-root": {
                        padding: "12px 16px",
                        fontSize: "0.95rem",
                        transition: "background-color 0.15s ease",
                        "&:hover": {
                          backgroundColor: "rgba(26, 86, 50, 0.08)",
                        },
                        "&.Mui-selected": {
                          backgroundColor: "rgba(26, 86, 50, 0.12)",
                          fontWeight: 600,
                          color: "#1A5632",
                          "&:hover": {
                            backgroundColor: "rgba(26, 86, 50, 0.18)",
                          },
                        },
                      },
                    },
                  },
                }}
              >
                {agentsList.map((agent) => (
                  <MenuItem key={agent.id} value={agent.id}>
                    {agent.agentName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}
        {onClose && onToggleExpand && (
          <IconButton
            onClick={onToggleExpand}
            sx={{
              marginLeft: "auto",
              marginRight: "1%",
              color: "white",
              fontSize: "3vmin",
              height: "auto",
              width: "auto",
            }}
            aria-label={isExpanded ? "Collapse chat" : "Expand chat"}
          >
            {isExpanded ? "🗕" : "⛶"}
          </IconButton>
        )}
        {onClose && (
          <IconButton
            onClick={onClose}
            sx={{
              marginLeft: 0,
              marginRight: "3%",
              color: "white",
              fontSize: "3vmin",
              height: "auto",
              width: "auto",
            }}
          >
            ✕
          </IconButton>
        )}
      </Box>

      {/* <Divider sx={{ borderColor: themeMode === "dark" ? "#033319" : "#f5f5f5" }} /> */}

      {/* Visually hidden heading for chat area */}
      <Typography
        variant="h2"
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: "hidden",
          clip: "rect(0,0,0,0)",
          border: 0,
        }}
        tabIndex={-1}
      >
        Chat Area
      </Typography>
      {/* Main content area */}
      <Box
        role="region"
        aria-label="Chat conversation"
        sx={{
          flex: 1,
          overflowY: "auto",
          paddingBottom: "140px",
          width: "100%",
        }}
      >
        <Box
          sx={{
            width: isFullscreenByWidth ? "70%" : "100%",
            // paddingX: isFullscreenByWidth ? "0rem" : "1rem",
            margin: "0 auto",
          }}
        >
          <ChatWindow
            conversation={conversation}
            showLoading={showLoading}
            chatEndRef={chatEndRef}
            onFeedbackSubmit={handleFeedbackSubmit}
            onOptionClick={handleOptionClick}
          />
        </Box>
      </Box>

      {/* Visually hidden heading for input area */}
      <Typography
        variant="h2"
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: "hidden",
          clip: "rect(0,0,0,0)",
          border: 0,
        }}
        tabIndex={-1}
      >
        Message Input
      </Typography>
      {/* Input form */}
      <Box
        component="form"
        role="form"
        aria-label="Message input area"
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          backgroundColor: themeMode === "dark" ? "#1A5632" : "#FFFFFF",
          borderTop: `1px solid ${themeMode === "dark" ? "#033319" : "#e0e0e0"}`,
        }}
      >
        <Typography
          variant="caption"
          color={themeMode === "dark" ? "#bcbcbc" : "#767676"}
          sx={{
            textAlign: "center",
            pt: 1,
            fontSize: "0.7rem",
          }}
        >
          Chatbots can make mistakes. Check important info.
        </Typography>

        <Box
          component="form"
          onSubmit={sendMessage}
          sx={{
            margin: "10px auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            width: isFullscreenByWidth ? "70%" : "100%",
            paddingX: "1rem",
            boxSizing: "border-box",
          }}
        >
          {/* Visually hidden label for accessibility */}
          <label
            htmlFor="chat-input"
            style={{
              position: "absolute",
              width: "1px",
              height: "1px",
              padding: 0,
              margin: "-1px",
              overflow: "hidden",
              clip: "rect(0,0,0,0)",
              border: 0,
            }}
          >
            Chat input
          </label>
          <TextField
            id="chat-input"
            fullWidth
            multiline
            maxRows={6}
            type="text"
            value={message}
            onChange={updateMessage}
            onKeyDown={handleKeyDown}
            placeholder="Ask me a question..."
            variant="outlined"
            hiddenLabel
            sx={{
              //Color around text field
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                backgroundColor: themeMode === "dark" ? "#033319" : "#f5f5f5",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: themeMode === "dark" ? "#011f0f" : "#d3d3d3",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: themeMode === "dark" ? "#011f0f" : "#d3d3d3",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: themeMode === "dark" ? "#011f0f" : "#d3d3d3",
                  borderWidth: "1px",
                },
              },
              //Color of text inside text field
              "& .MuiInputBase-input": {
                backgroundColor: themeMode === "dark" ? "#033319" : "#f5f5f5",
                color: themeMode === "dark" ? "#bcbcbc" : "#767676",
                WebkitTextFillColor: themeMode === "dark" ? "#bcbcbc" : "#767676", // helps in Safari/autofill cases
              },
              "& fieldset": {
                borderRadius: "10px",
              },
              "& fieldset legend": {
                width: 0,
              },
            }}
          />
          <Button
            variant="contained"
            type="submit"
            onClick={sendMessage}
            endIcon={<FontAwesomeIcon icon={faPaperPlane} />}
            sx={{
              mt: 1,
              borderRadius: "5px",
              backgroundColor: themeMode === "dark" ? "#fdb913" : "#1A5632",
              color: themeMode === "dark" ? "#033319" : "#f5f5f5", // text + icon color
              "&:hover": {
                backgroundColor: themeMode === "dark" ? "#fdb913" : "#033319",
                color: themeMode === "dark" ? "#033319" : "#f5f5f5",
              },
              "& .MuiButton-endIcon": {
                color: "inherit",
              },
            }}
          >
            Send
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default App;
